import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'

import { Text } from '@digital-twins/design-system/components/ui/Text'
import { UserAvatar } from '@digital-twins/design-system/components/ui/UserAvatar'
import { cn } from '@digital-twins/design-system/lib/utils'
import type { PlotViewport } from '../contract'
import { TextClamp } from '../../components/text/TextClamp'
import { QuoteListQuote, QuoteListSpeaker } from '@digital-twins/slide-schemas/generated/plot-types'
import { StoredPlotPayload } from '../contract'
import { QuoteGallery } from './QuoteGallery'
import { glossedQuote } from './glossedQuote'

/**
 * QuoteListPlot: the `quote-list` kind (plots plan S).
 *
 * Two layouts, each today's pixels moved as is:
 *
 * - `card` at slide size: the bar slides' quote card body (accent dot, the
 *   verbatim in curly quotes over 2 to 3 lines, one attribution line printing
 *   `name` or `name, role`). The frame and the stored header band are slide
 *   chrome, drawn by `SlideQuoteCard`.
 * - `carousel` at card size: the summary quotes card body (question line,
 *   theme line, verbatim, speaker row, dots and arrows).
 * - `gallery` at card size: the quick-study quotes view (`QuoteGallery`,
 *   wave 7); hosts that need its host hooks draw `QuoteGallery` directly.
 *
 * Draws what Python stamped: the speaker as resolved (initials only when the
 * producer stamped them, so no circle for an unresolved speaker), an Other
 * quote's theme line as `labels.plot_other`, never the codeframe's title. The
 * host supplies what analysis output never stores: the speaker's image (keyed
 * by `respondentId`), the actions beside the speaker, and the app-language
 * words of the arrows and dots. Never imports the frontend's `@/lib`.
 */

const LINE_DARK = 'var(--slide-cover-bg)'
const BRAND_EMPHASIS = 'var(--slide-accent)'
const ATTRIBUTION_COLOR = 'var(--slide-text-secondary)'
/** How many carousel dots show at once; the window slides with the current quote. */
export const QUOTE_DOT_WINDOW = 7

export type QuoteListData = StoredPlotPayload<'quote-list'>['data']
export type QuoteListLayout = 'card' | 'carousel' | 'gallery'

export interface QuoteListNavLabels {
  prev: string
  next: string
  goTo: (position: number) => string
}

export interface QuoteListSpeakerAvatarArgs {
  quote: QuoteListQuote
  speaker: QuoteListSpeaker
  name: string
  /** What the plot draws when the host has no image: the initials circle, or null. */
  fallback: ReactNode
}

export interface QuoteListPlotProps {
  viewport?: PlotViewport
  data: QuoteListData
  size: 'slide' | 'card'
  layout: QuoteListLayout
  renderMode?: 'interactive' | 'static'
  /** Carousel: drawn first inside the card body (the host's avatar stack). */
  header?: ReactNode
  /** Carousel: the question line for a quote (the host may resolve it from the survey). */
  questionTextOf?: (quote: QuoteListQuote) => string | null | undefined
  /** Carousel: the speaker's avatar; the host resolves the image by `respondentId`. */
  renderSpeakerAvatar?: (args: QuoteListSpeakerAvatarArgs) => ReactNode
  /** Carousel: what sits after the speaker's name (version chip, chat button). */
  renderSpeakerActions?: (quote: QuoteListQuote, name: string) => ReactNode
  /** Carousel: arrow and dot words, in the viewer's app language. */
  navLabels?: QuoteListNavLabels
}

/** The printed attribution line: `name`, or `name, role`. */
export function quoteAttribution(speaker: QuoteListSpeaker | null | undefined): string {
  const name = speaker?.name?.trim() ?? ''
  const role = speaker?.role?.trim() ?? ''
  if (name && role) {
    return `${name}, ${role}`
  }
  return name || role
}

/**
 * Where the dot window starts so `currentIndex` stays inside it: slide by the
 * overshoot when the index leaves either edge, and never past the last full
 * window. Pure so the render and the effect agree on the same start.
 */
export function clampDotWindowStart(start: number, currentIndex: number, total: number): number {
  const maxStart = Math.max(0, total - QUOTE_DOT_WINDOW)
  let next = start
  if (currentIndex < next) next = currentIndex
  if (currentIndex >= next + QUOTE_DOT_WINDOW) next = currentIndex - QUOTE_DOT_WINDOW + 1
  return Math.min(Math.max(0, next), maxStart)
}

function QuoteCardBody(props: { data: QuoteListData; renderMode?: 'interactive' | 'static'; viewport?: PlotViewport }) {
  const { data } = props

  const quote = data.quotes[0]
  const text = quote?.text ?? ''
  const attribution = quoteAttribution(quote?.speaker)

  return (
    <div data-testid="quote-list-card" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', background: BRAND_EMPHASIS, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <TextClamp
          data-testid="quote-list-text"
          renderMode={props.renderMode}
          minLines={2}
          maxLines={props.viewport && props.renderMode === 'static' ? undefined : 3}
          lineHeight="1.4"
          style={{ fontFamily: 'var(--ds-font-display)', fontSize: 18, color: LINE_DARK }}
        >
          {text ? <>“{glossedQuote(text, quote?.textRuns)}”</> : ''}
        </TextClamp>
        {attribution ? (
          <TextClamp
            data-testid="quote-list-speaker"
            renderMode={props.renderMode}
            minLines={1}
            maxLines={props.viewport && props.renderMode === 'static' ? undefined : 1}
            lineHeight="1.6"
            style={{ fontSize: 16, color: ATTRIBUTION_COLOR, marginTop: 4 }}
          >
            {attribution}
          </TextClamp>
        ) : null}
      </div>
    </div>
  )
}

function QuoteCarousel(props: QuoteListPlotProps) {
  const { data, header, questionTextOf, renderSpeakerAvatar, renderSpeakerActions, navLabels } = props

  const quotes = data.quotes
  const [currentIndex, setCurrentIndex] = useState(0)
  const [windowStart, setWindowStart] = useState(0)
  // Which way the last page turn went, so the incoming quote slides in from
  // the side the reader moved towards. Forward by default (dots and first paint).
  const [slideDirection, setSlideDirection] = useState<'forward' | 'back'>('forward')

  const goTo = useCallback((index: number, direction: 'forward' | 'back') => {
    setSlideDirection(direction)
    setCurrentIndex(index)
  }, [])

  const advance = useCallback(() => {
    setSlideDirection('forward')
    setCurrentIndex((prev) => (prev + 1) % quotes.length)
  }, [quotes.length])

  const retreat = useCallback(() => {
    setSlideDirection('back')
    setCurrentIndex((prev) => (prev - 1 + quotes.length) % quotes.length)
  }, [quotes.length])

  // The dot window follows the current quote: it slides by one when the
  // index steps past either edge and snaps back when the arrows wrap around.
  useEffect(() => {
    setWindowStart((prev) => clampDotWindowStart(prev, currentIndex, quotes.length))
  }, [currentIndex, quotes.length])

  if (quotes.length === 0) return null

  const quote = quotes[Math.min(currentIndex, quotes.length - 1)]
  const speaker = quote.speaker ?? null
  const name = speaker?.name?.trim() ?? ''
  const questionText = questionTextOf ? questionTextOf(quote) : quote.questionText
  const otherLabel = data.labels.plot_other

  // Render from a clamped start rather than the stored one so the first paint
  // after a quotes change never shows a stale window before the effect runs.
  const dotWindowStart = clampDotWindowStart(windowStart, currentIndex, quotes.length)
  const windowEnd = Math.min(quotes.length, dotWindowStart + QUOTE_DOT_WINDOW)
  const visibleDotIndexes = Array.from(
    { length: windowEnd - dotWindowStart },
    (_, offset) => dotWindowStart + offset,
  )

  // No stamped initials (an unresolved speaker): no circle.
  const initialsAvatar = speaker?.initials ? (
    <span data-testid="summary-quote-speaker-initials">
      <UserAvatar initials={speaker.initials} name={name} size="sm" className="h-12 w-12" />
    </span>
  ) : null
  const avatar =
    speaker && renderSpeakerAvatar
      ? renderSpeakerAvatar({ quote, speaker, name, fallback: initialsAvatar })
      : initialsAvatar

  const themeLine = quote.isOther ? (
    otherLabel ? (
      <Text size="sm" className="font-medium text-muted-foreground" data-testid="summary-quote-theme-other">
        {otherLabel}
      </Text>
    ) : null
  ) : quote.themeLabel ? (
    <Text size="sm" className="font-medium text-muted-foreground" data-testid="summary-quote-theme">
      {quote.themeLabel}
    </Text>
  ) : null

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {header}

      {/*
        Keyed on the index so a page turn remounts the body and plays one
        short fade-and-slide, a subtle cue that the quote changed rather than
        a card transition. Reduced-motion readers get the swap with no motion.
      */}
      <div
        key={currentIndex}
        data-testid="summary-quote-body"
        data-slide-direction={slideDirection}
        className={cn(
          'flex flex-col gap-4 animate-in fade-in duration-300 ease-out motion-reduce:animate-none',
          slideDirection === 'forward' ? 'slide-in-from-right-2' : 'slide-in-from-left-2',
        )}
      >
      <div className="flex flex-col gap-1.5">
        {/*
          The question this verbatim answers, above the quote and above the
          theme. Per quote, not per card: the card's verbatims are selected
          across every open-end the study coded, so one heading over the
          carousel would attribute all of them to whichever prompt came first.
        */}
        {questionText && (
          <Text
            size="sm"
            className="font-medium text-foreground"
            data-testid="summary-quote-question"
          >
            {questionText}
          </Text>
        )}
        {themeLine}
        <Text
          className="font-display text-lg leading-[1.6] text-foreground line-clamp-6"
          data-testid="summary-quote-text"
        >
          &ldquo;{glossedQuote(quote.text ?? '', quote.textRuns)}&rdquo;
        </Text>
      </div>

      {name && (
        <div className="flex items-center gap-3" data-testid="summary-quote-speaker-row">
          {avatar}

          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-medium text-foreground truncate">{name}</span>
            {speaker?.role && (
              <span className="text-xs text-muted-foreground truncate">{speaker.role}</span>
            )}
          </div>

          {renderSpeakerActions ? renderSpeakerActions(quote, name) : null}
        </div>
      )}
      </div>

      {quotes.length > 1 && (
        <div className="mt-auto flex items-center justify-between gap-2 min-w-0">
          <button
            type="button"
            onClick={retreat}
            data-testid="quote-prev"
            className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center shadow-home-float transition-transform duration-200 hover:-translate-y-0.5 flex-shrink-0"
            aria-label={navLabels?.prev}
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
          </button>

          <div
            className="flex items-center gap-1.5 min-w-0 overflow-hidden"
            data-testid="summary-quote-dots"
          >
            {visibleDotIndexes.map((idx) => {
              // The outermost dot on a side that hides more quotes draws
              // small, the carousel cue that the row continues past it.
              const isEdgeCue =
                (idx === dotWindowStart && dotWindowStart > 0) ||
                (idx === windowEnd - 1 && windowEnd < quotes.length)
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => goTo(idx, idx >= currentIndex ? 'forward' : 'back')}
                  data-testid={`summary-quote-dot-${idx}`}
                  data-edge-cue={isEdgeCue ? 'true' : 'false'}
                  aria-label={navLabels?.goTo(idx + 1)}
                  className={cn(
                    'rounded-full transition-all flex-shrink-0',
                    idx === currentIndex
                      ? 'w-5 h-1.5 bg-foreground'
                      : isEdgeCue
                        ? 'w-1 h-1 bg-muted-foreground/25 hover:bg-muted-foreground/60'
                        : 'w-1.5 h-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/70',
                  )}
                />
              )
            })}
          </div>

          <button
            type="button"
            onClick={advance}
            data-testid="quote-next"
            className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center shadow-home-float transition-transform duration-200 hover:-translate-y-0.5 flex-shrink-0"
            aria-label={navLabels?.next}
          >
            <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>
      )}
    </div>
  )
}

export function QuoteListPlot(props: QuoteListPlotProps) {
  const { data, layout } = props

  if (layout === 'card') {
    return <QuoteCardBody data={data} renderMode={props.renderMode} viewport={props.viewport} />
  }
  if (layout === 'gallery') {
    return <QuoteGallery data={data} renderMode={props.renderMode} />
  }
  return <QuoteCarousel {...props} />
}
